/**
 * Alliance Leaderboard Dialog
 * Displays ranked alliance data: prestige, territory, members, HQ tier
 */

import Phaser from 'phaser';
import { MMONetworkManager, MMOEvents } from '../../managers/MMONetworkManager';
import { EventBus } from '../../utils/EventBus';
import { Colors, CSSColors, Fonts, FontSize, Space, createDialogChrome, closeDialog } from './GameUITheme';
import { I18n } from '../../i18n/I18nService';
import { isMobile } from '../../utils/ResponsiveHelper';

interface LeaderboardEntry {
  rank: number;
  allianceId: number;
  allianceName: string;
  allianceTag: string;
  prestigeScore: number;
  territoryRadius: number;
  memberCount: number;
  hqTier: string;
}

// Medal colors for top 3
const MEDAL_COLORS = {
  1: '#FFD700', // gold
  2: '#C0C0C0', // silver
  3: '#CD7F32', // bronze
} as Record<number, string>;

const ROW_HEIGHT = 36;
const HEADER_Y = 60;
const TABLE_TOP = 95;
export class AllianceLeaderboardDialog extends Phaser.GameObjects.Container {
  private dw: number;
  private dh: number;
  private mmoNetwork: MMONetworkManager;

  private contentContainer: Phaser.GameObjects.Container;
  private noDataText?: Phaser.GameObjects.Text;
  private loadingText?: Phaser.GameObjects.Text;
  private entries: LeaderboardEntry[] = [];

  // Event handler refs
  private onLeaderboardHandler: (payload: any) => void;

  constructor(scene: Phaser.Scene, mmoNetwork: MMONetworkManager) {
    const mobile = isMobile(scene);
    const dw = mobile ? 360 : 700;
    const dh = mobile ? 520 : 560;
    const { width, height } = scene.cameras.main;
    super(scene, (width - dw) / 2, (height - dh) / 2);
    this.dw = dw;
    this.dh = dh;
    this.mmoNetwork = mmoNetwork;

    createDialogChrome(scene, this, dw, dh, {
      title: I18n.t('dialog.leaderboard'),
      onClose: () => this.close(),
    });

    // Content area
    this.contentContainer = scene.add.container(0, TABLE_TOP);
    this.add(this.contentContainer);

    // Column headers
    this.createColumnHeaders();

    // Loading indicator
    this.loadingText = scene.add.text(dw / 2, TABLE_TOP + 60, '...', {
      fontSize: FontSize.md,
      color: CSSColors.textMuted,
      fontFamily: Fonts.body,
    });
    this.loadingText.setOrigin(0.5);
    this.add(this.loadingText);

    // Refresh button (bottom-right)
    this.createRefreshButton();

    // Listen for response
    this.onLeaderboardHandler = (payload: any) => this.onLeaderboardData(payload);
    EventBus.on(MMOEvents.LEADERBOARD_RESPONSE, this.onLeaderboardHandler);

    // Request data
    this.mmoNetwork.requestLeaderboard();

    this.setDepth(2000);
    scene.add.existing(this);
  }

  // ═══════════════════════════════════════════
  //  COLUMN HEADERS
  // ═══════════════════════════════════════════
  private createColumnHeaders(): void {
    const mobile = isMobile(this.scene);
    const cols = this.getColumnLayout(mobile);
    const y = HEADER_Y;

    const headerStyle: Phaser.Types.GameObjects.Text.TextStyle = {
      fontSize: FontSize.xs,
      color: CSSColors.textMuted,
      fontFamily: Fonts.heading,
    };

    // Separator line under header
    const g = this.scene.add.graphics();
    g.lineStyle(1, Colors.borderDim, 0.4);
    g.lineBetween(Space.md, y + 18, this.dw - Space.md, y + 18);
    this.add(g);

    for (const col of cols) {
      const t = this.scene.add.text(col.x, y, col.label, headerStyle);
      t.setOrigin(0, 0.5);
      this.add(t);
    }
  }

  private getColumnLayout(mobile: boolean): { x: number; label: string; width: number }[] {
    if (mobile) {
      return [
        { x: 14, label: '#', width: 28 },
        { x: 42, label: I18n.t('leaderboard.alliance'), width: 120 },
        { x: 170, label: I18n.t('leaderboard.prestige'), width: 70 },
        { x: 248, label: I18n.t('leaderboard.members'), width: 60 },
        { x: 312, label: I18n.t('leaderboard.hq_tier'), width: 48 },
      ];
    }
    return [
      { x: 20, label: '#', width: 36 },
      { x: 56, label: I18n.t('leaderboard.alliance'), width: 180 },
      { x: 250, label: I18n.t('leaderboard.prestige'), width: 90 },
      { x: 360, label: I18n.t('leaderboard.territory'), width: 80 },
      { x: 460, label: I18n.t('leaderboard.members'), width: 80 },
      { x: 560, label: I18n.t('leaderboard.hq_tier'), width: 100 },
    ];
  }

  // ═══════════════════════════════════════════
  //  DATA HANDLING
  // ═══════════════════════════════════════════
  private onLeaderboardData(payload: any): void {
    if (this.loadingText) {
      this.loadingText.destroy();
      this.loadingText = undefined;
    }

    const alliances: any[] = payload?.alliances || payload?.leaderboard || [];
    if (alliances.length === 0) {
      this.showNoData();
      return;
    }

    this.entries = alliances.map((a: any, i: number) => ({
      rank: a.rank ?? i + 1,
      allianceId: a.allianceId ?? a.id ?? 0,
      allianceName: a.allianceName ?? a.name ?? 'Unknown',
      allianceTag: a.allianceTag ?? a.tag ?? '???',
      prestigeScore: a.prestigeScore ?? a.prestige ?? 0,
      territoryRadius: a.territoryRadius ?? a.territory ?? 0,
      memberCount: a.memberCount ?? a.members ?? 0,
      hqTier: a.hqTier ?? a.hq_tier ?? '-',
    }));

    this.renderRows();
  }

  private showNoData(): void {
    if (this.noDataText) return;
    this.noDataText = this.scene.add.text(this.dw / 2, TABLE_TOP + 60, I18n.t('leaderboard.no_data'), {
      fontSize: FontSize.md,
      color: CSSColors.textMuted,
      fontFamily: Fonts.body,
    });
    this.noDataText.setOrigin(0.5);
    this.contentContainer.add(this.noDataText);
  }

  // ═══════════════════════════════════════════
  //  RENDER TABLE ROWS
  // ═══════════════════════════════════════════
  private renderRows(): void {
    // Clear existing content
    this.contentContainer.removeAll(true);
    if (this.noDataText) { this.noDataText = undefined; }

    const mobile = isMobile(this.scene);
    const cols = this.getColumnLayout(mobile);
    const maxVisible = Math.floor((this.dh - TABLE_TOP - 50) / ROW_HEIGHT);

    for (let i = 0; i < Math.min(this.entries.length, maxVisible); i++) {
      const entry = this.entries[i];
      const rowY = i * ROW_HEIGHT;

      // Alternating row background
      if (i % 2 === 0) {
        const rowBg = this.scene.add.graphics();
        rowBg.fillStyle(Colors.bgCard, 0.3);
        rowBg.fillRect(Space.md, rowY, this.dw - Space.md * 2, ROW_HEIGHT - 2);
        this.contentContainer.add(rowBg);
      }

      // Top-3 highlight bar
      if (entry.rank <= 3) {
        const hlBg = this.scene.add.graphics();
        const hlColor = entry.rank === 1 ? 0xFFD700 : entry.rank === 2 ? 0xC0C0C0 : 0xCD7F32;
        hlBg.fillStyle(hlColor, 0.06);
        hlBg.fillRect(Space.md, rowY, this.dw - Space.md * 2, ROW_HEIGHT - 2);
        this.contentContainer.add(hlBg);
      }

      const textColor = MEDAL_COLORS[entry.rank] || CSSColors.textLight;
      const textStyle: Phaser.Types.GameObjects.Text.TextStyle = {
        fontSize: FontSize.sm,
        color: textColor,
        fontFamily: Fonts.body,
      };

      const nameStyle: Phaser.Types.GameObjects.Text.TextStyle = {
        fontSize: FontSize.sm,
        color: entry.rank <= 3 ? (MEDAL_COLORS[entry.rank] || CSSColors.textWhite) : CSSColors.textWhite,
        fontFamily: Fonts.heading,
      };

      // Rank
      const rankPrefix = entry.rank <= 3 ? ['', '\u{1F947}', '\u{1F948}', '\u{1F949}'][entry.rank] + ' ' : '';
      const rankText = this.scene.add.text(cols[0].x, rowY + ROW_HEIGHT / 2, `${rankPrefix}${entry.rank}`, textStyle);
      rankText.setOrigin(0, 0.5);
      this.contentContainer.add(rankText);

      // Alliance name [TAG]
      const nameStr = `[${entry.allianceTag}] ${entry.allianceName}`;
      const nameText = this.scene.add.text(cols[1].x, rowY + ROW_HEIGHT / 2, nameStr, nameStyle);
      nameText.setOrigin(0, 0.5);
      // Truncate if too wide
      if (nameText.width > cols[1].width) {
        nameText.setStyle({ ...nameStyle, fixedWidth: cols[1].width });
        nameText.setCrop(0, 0, cols[1].width, ROW_HEIGHT);
      }
      this.contentContainer.add(nameText);

      // Prestige
      const prestigeText = this.scene.add.text(cols[2].x, rowY + ROW_HEIGHT / 2, entry.prestigeScore.toLocaleString(), textStyle);
      prestigeText.setOrigin(0, 0.5);
      this.contentContainer.add(prestigeText);

      if (!mobile) {
        // Territory radius
        const terText = this.scene.add.text(cols[3].x, rowY + ROW_HEIGHT / 2, `${entry.territoryRadius}`, textStyle);
        terText.setOrigin(0, 0.5);
        this.contentContainer.add(terText);
      }

      // Members
      const memIdx = mobile ? 3 : 4;
      const memText = this.scene.add.text(cols[memIdx].x, rowY + ROW_HEIGHT / 2, `${entry.memberCount}`, textStyle);
      memText.setOrigin(0, 0.5);
      this.contentContainer.add(memText);

      // HQ Tier
      const hqIdx = mobile ? 4 : 5;
      const hqText = this.scene.add.text(cols[hqIdx].x, rowY + ROW_HEIGHT / 2, entry.hqTier, textStyle);
      hqText.setOrigin(0, 0.5);
      this.contentContainer.add(hqText);
    }
  }

  // ═══════════════════════════════════════════
  //  REFRESH BUTTON
  // ═══════════════════════════════════════════
  private createRefreshButton(): void {
    const btnW = 90;
    const btnH = 30;
    const bx = this.dw - btnW - Space.lg;
    const by = this.dh - btnH - Space.md;

    const btnContainer = this.scene.add.container(bx, by);

    const bg = this.scene.add.graphics();
    bg.fillStyle(Colors.bgElevated, 0.9);
    bg.fillRoundedRect(0, 0, btnW, btnH, 4);
    bg.lineStyle(1, Colors.cyan, 0.2);
    bg.strokeRoundedRect(0, 0, btnW, btnH, 4);

    const label = this.scene.add.text(btnW / 2, btnH / 2, I18n.t('leaderboard.refresh'), {
      fontSize: FontSize.sm,
      color: CSSColors.cyan,
      fontFamily: Fonts.heading,
    });
    label.setOrigin(0.5);

    const hitZone = this.scene.add.zone(0, 0, btnW, btnH).setOrigin(0, 0);
    hitZone.setInteractive(
      new Phaser.Geom.Rectangle(0, 0, btnW, btnH),
      Phaser.Geom.Rectangle.Contains,
    );

    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(Colors.bgCardHover, 0.9);
      bg.fillRoundedRect(0, 0, btnW, btnH, 4);
      bg.lineStyle(1, Colors.cyan, 0.4);
      bg.strokeRoundedRect(0, 0, btnW, btnH, 4);
      this.scene.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(Colors.bgElevated, 0.9);
      bg.fillRoundedRect(0, 0, btnW, btnH, 4);
      bg.lineStyle(1, Colors.cyan, 0.2);
      bg.strokeRoundedRect(0, 0, btnW, btnH, 4);
      this.scene.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', () => {
      // Clear and re-request
      this.contentContainer.removeAll(true);
      this.noDataText = undefined;
      this.loadingText = this.scene.add.text(this.dw / 2, 60, '...', {
        fontSize: FontSize.md,
        color: CSSColors.textMuted,
        fontFamily: Fonts.body,
      });
      this.loadingText.setOrigin(0.5);
      this.add(this.loadingText);
      this.mmoNetwork.requestLeaderboard();
    });

    btnContainer.add([bg, label, hitZone]);
    this.add(btnContainer);
  }

  // ═══════════════════════════════════════════
  //  CLOSE
  // ═══════════════════════════════════════════
  public close(): void {
    EventBus.off(MMOEvents.LEADERBOARD_RESPONSE, this.onLeaderboardHandler);
    closeDialog(this.scene, this);
  }
}
